import { Box, Button, HStack, Input, Radio, RadioGroup, Spinner, Stack, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useDisclosure } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { adminGetMedicinesByKeyword } from "../../../api";
import AdminOnly from "../../../components/Auth/AdminOnly";
import MyIcon from "../../../components/Icon/MyIcon";
import { colors } from "../../../styles";
import { IAdminMedicineByKeyword, IAdminMedicineByKeywordList } from "../../../types";
import MedicineByKeywordModal from "./MedicineByKeywordModal";
import UpdateConnectionModal from "./UpdateConnectionModal";



export default function KeywordsByMedicine() {

    /* pagination */

    const [page, setPage] = useState(1);
    const size = 100;

    /* form */

    interface ISearchForm {
        searchKeyword: string;
    }

    const { register, handleSubmit } = useForm<ISearchForm>({
        mode: "onChange",
    });

    const [searchKeyword, setSearchKeyword] = useState<string | null>(null);
    const [isDisplay, setIsDisplay] = useState<string>("true");

    const onSubmit = ({ searchKeyword }: ISearchForm) => {
        setPage(1);
        setSearchKeyword(searchKeyword);
    }
    
    /* query */

    const { data: medicineListData, isLoading: isMedicineListDataLoading } = useQuery<IAdminMedicineByKeywordList>({
        queryKey: ['adminGetMedicinesByKeyword', page, size, isDisplay, searchKeyword],
        queryFn: () => adminGetMedicinesByKeyword({ page, size, isDisplay, searchKeyword }),
    });


    console.log("💊 medicineByKeywordListData:", medicineListData);

    const totalPages = useMemo(() => medicineListData?.totalPages ?? 0, [medicineListData]);
    const currentPage = useMemo(() => medicineListData?.currentPage ?? 0, [medicineListData]);

    /* pagination */

    const pages = useMemo(() => {
        const startPage = Math.max(page - 4, 1);
        const endPage = Math.min(page + 5, totalPages);
        return Array.from({ length: Math.max(0, endPage - startPage + 1) }, (_, idx) => startPage + idx);
    }, [page, totalPages]);

    /* columns */

    const columns = [
        { key: "isDisplay", label: "isDisplay" },
        { key: "nameKor", label: "한글명" },
        { key: "keywords", label: "연결된 키워드" },
        { key: "seqNo", label: "seqNo" },
        { key: "standardCode", label: "표준 코드" },
        { key: "packageUnit", label: "포장 단위" },
        { key: "ingredientCode", label: "ingredientCode" },
        { key: "dosageRoute", label: "복약 경로" },
        { key: "ingredientInfo", label: "성분 정보" },
    ]

    /* update connection modal */

    const { isOpen: isOpenUpdateConnectionModal, onOpen: onOpenUpdateConnectionModal, onClose: onCloseUpdateConnectionModal } = useDisclosure();
    const [activeConnectionId, setActiveConnectionId] = useState<string | null>(null);

    /* keyword Box */

    const KeywordBox = (connectionId: string, keyword:string, weight:number) => {
        return (
            <Button
                key={keyword}
                size={"md"} 
                variant={"outline"}
                borderColor={colors.blue}
                marginX={1}
                onClick={() => {
                    setActiveConnectionId(connectionId);
                    onOpenUpdateConnectionModal();
                }}
            >
                <Text>{keyword}</Text>
                <Text fontSize={12} fontWeight={500} color={colors.blue} marginLeft={1}>{weight}</Text>
            </Button>
        )
    }

    /* useDisclosure */

    const { isOpen: isOpenConnectionModal, onOpen: onOpenConnectionModal, onClose: onCloseConnectionModal } = useDisclosure();
    const [activeMedicineId, setActiveMedicineId] = useState<string | null>(null);

    /* keyword Stack */

    const KeywordStack = (medicine: IAdminMedicineByKeyword) => {
        return (
            <HStack width={"100%"}>
                <Box as="button" onClick={() => {
                    setActiveMedicineId(medicine.id);
                    onOpenConnectionModal();
                }}>
                    <MyIcon src={"/svg/plus-circle.svg"} alt="plus" width={24} height={24} />
                </Box>
                {medicine.connections.map((connection) => KeywordBox(`${medicine.id}*${connection.keyword.id}`, connection.keyword.keyword, connection.weight))}
            </HStack>
        )
    }

    /* render */


    return (
        <AdminOnly>
            <Stack width={"100%"} padding={10}>
                <HStack justifyContent={"space-between"} alignItems={"center"} gap={10}>
                    <HStack justifyContent={"flex-start"} alignItems={"center"} gap={3}>
                        <Text fontSize={28} fontWeight={700} color={colors.black}>약품으로 검색</Text>
                        <Text fontSize={28} fontWeight={400} color={colors.gray}>{medicineListData?.totalCount}</Text>
                    </HStack>
                    <HStack gap={3}>
                    </HStack>
                </HStack>

                <RadioGroup value={isDisplay} onChange={setIsDisplay}>
                    <HStack spacing={6} marginY={4}>
                        <Box
                            paddingX={3}
                            paddingY={1}
                            backgroundColor={colors.light}
                            borderRadius={10}
                        >
                            <Text fontSize={12} fontWeight={500} color={colors.blue}>ISDISPLAY</Text>
                        </Box>
                        <Radio value="all" colorScheme="blue">모두</Radio>
                        <Radio value="true" colorScheme="blue">TRUE</Radio>
                        <Radio value="false" colorScheme="blue">FALSE</Radio>
                    </HStack>
                </RadioGroup>

                {/* search form */}

                <HStack as="form" onSubmit={handleSubmit(onSubmit)} marginY={10}>
                    <Input 
                        {...register("searchKeyword", {required:false})}
                        type="text" 
                        placeholder="nameKor, ingredientInfo, standardCode, seqNo, ingredientCode, dosageRoute or [keyword]" 
                        width={"50%"} 
                    />
                    <Box as="button" type="submit">
                        <MyIcon src={"/svg/search.svg"} alt="search" width={24} height={24} />
                    </Box>
                </HStack>

                {/* table */}

                {!isMedicineListDataLoading ? 
                    <TableContainer marginTop={10}>
                        <Table variant='simple' scrollBehavior="smooth">
                            <Thead backgroundColor={colors.g5}>
                                <Tr>
                                    {columns.map((column) => (  
                                        <Th key={column.key}>{column.label}</Th>
                                    ))}
                                </Tr>
                            </Thead>
                            <Tbody>
                                {medicineListData?.medicines.map((medicine) => (
                                    <Tr key={medicine.id}>
                                        {columns.map((column, index) => (
                                            <Td key={index}>
                                                {column.key === "keywords" ? KeywordStack(medicine) : 
                                                    column.key === "isDisplay" ? (medicine.isDisplay ? "O" : "X") : String(medicine[column.key as keyof IAdminMedicineByKeyword] ?? "-")}
                                            </Td>
                                        ))}
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </TableContainer>
                        : 
                    <Spinner color={colors.blue} />
                }

                {/* single medicine keyword modal */}   

                {activeMedicineId && (
                    <MedicineByKeywordModal 
                        isOpen={isOpenConnectionModal} 
                        onClose={() => {
                            onCloseConnectionModal();
                            setActiveMedicineId(null);
                        }} 
                        medicineId={activeMedicineId} 
                        page={page}
                        size={size}
                        isDisplay={isDisplay}
                        searchKeyword={searchKeyword}
                    />
                )}

                {/* update connection modal */}

                {activeConnectionId && (
                    <UpdateConnectionModal 
                        isOpen={isOpenUpdateConnectionModal} 
                        onClose={() => {
                            onCloseUpdateConnectionModal();
                            setActiveConnectionId(null);
                        }}
                        connectionId={activeConnectionId}
                        page={page}
                        size={size}
                        isDisplay={isDisplay}
                        searchKeyword={searchKeyword}
                    />
                )}

                {/* pagination */}

                <HStack justifyContent={"center"} marginTop={5} >
                    {/* 이전 페이지 버튼 */}
                    {page > 1 && (
                        <Button size={"xs"} variant={"ghost"} onClick={() => setPage(page - 1)}>Previous</Button>
                    )}
            
                    {/* 첫 페이지와 '...' */}
                    {page > 5 && (
                        <>
                            <Button size={"xs"} variant={"ghost"} onClick={() => setPage(1)}>1</Button>
                            <Text>...</Text>
                        </>
                    )}
            
                    {/* 페이지 버튼들 */}
                    {pages.map((page) => (
                        <Button
                            key={page}
                            size={"xs"} variant={"ghost"}
                            onClick={() => setPage(page)}
                            backgroundColor={page === currentPage ? colors.blue : ""}
                            color={page === currentPage ? colors.white : ""}
                        >
                            {page}
                        </Button>
                    ))}
            
                    {/* 마지막 페이지와 '...' */}
                    {page < totalPages - 4 && (
                        <>
                            <Text>...</Text>
                            <Button size={"xs"} variant={"ghost"} onClick={() => setPage(totalPages)}>{totalPages}</Button>
                        </>
                    )}
            
                    {/* 다음 페이지 버튼 */}
                    {page < totalPages && (
                        <Button size={"xs"} variant={"ghost"} onClick={() => setPage(page + 1)}>Next</Button>
                    )}
                </HStack>
            </Stack>
        </AdminOnly>
    )
}